import React, { useEffect } from 'react'

import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import { useBodyOverlayedClassHandler } from '@hooks/useBodyOverlayedClassHandler'
import {
  approveAgentSourcedCreator,
  rejectAgentSourcedCreator,
  shortlistAgentSourcedCreator,
  undoAgentSourcedCreatorAction,
  unshortlistAgentSourcedCreator,
} from '@store/agentSourcedCreators/actions'
import { selectAgentSourcedCreatorActionLoading } from '@store/agentSourcedCreators/selectors'
import Theme from '@theme'
import { Icon } from '@tribegroup'
import { IAgentSourcedCreator } from '../types'
import {
  Backdrop,
  BannerLayer,
  CloseButton,
  DrawerBody,
  DrawerPanel,
  DrawerSection,
  DrawerSectionTitle,
  ScoreBreakdownTuck,
  ScoreSummaryStack,
} from './AgentSourcedCreatorDrawer.styled'
import AIRationaleBanner from './AIRationaleBanner'
import AudiencePanel from './AudiencePanel'
import Header from './Header'
import ScoreBreakdown from './ScoreBreakdown'
import SelectedWorks from './SelectedWorks'

interface IProps {
  creator: IAgentSourcedCreator | undefined
  campaignId: number
  onClose: () => void
  // Mirrors the same prop on AgentSourcedCreatorRow so the drawer and the row it
  // opened from agree about the decision pill (BRAND-9587).
  showDecisionPill?: boolean
  // BRAND-9635: opt in to the sourced creator email. Off by default because the
  // unauthenticated /campaign-creators/:token page renders this same drawer — the
  // omission there is the gate, so forgetting the prop can only under-share.
  showSourcedEmail?: boolean
}

const AgentSourcedCreatorDrawer: React.FC<IProps> = ({
  creator,
  campaignId,
  onClose,
  showDecisionPill = true,
  showSourcedEmail = false,
}) => {
  const dispatch = useDispatch()
  const isActionLoading = useSelector(selectAgentSourcedCreatorActionLoading(creator?.id ?? 0))
  const open = Boolean(creator)

  // Lock background scroll while the drawer is open so scrolling inside the
  // drawer can't chain through to the creator list behind it (BRAND-9370).
  useBodyOverlayedClassHandler(open)

  useEffect(() => {
    if (!open) return undefined
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!creator) return null

  const approveLink = creator.links?.find((l) => l.rel === 'approve')
  const rejectLink = creator.links?.find((l) => l.rel === 'reject')
  const undoLink = creator.links?.find((l) => l.rel === 'undo')
  const shortlistLink = creator.links?.find((l) => l.rel === 'shortlist')
  const unshortlistLink = creator.links?.find((l) => l.rel === 'unshortlist')

  // Drawer stays open after dispatch. The reducer updates the creator's status
  // + HATEOAS links on success, so the Header's button set re-renders in place
  // (Approve/Reject disappear, Undo appears). The list view feeds this drawer
  // by selectedCreatorId → redux, so the prop refresh is automatic.
  const handleApprove = approveLink
    ? (): void => {
        dispatch(
          approveAgentSourcedCreator({
            creatorId: creator.id,
            campaignId,
            href: approveLink.href,
            method: approveLink.method,
          }),
        )
      }
    : undefined

  const handleReject = rejectLink
    ? (): void => {
        dispatch(
          rejectAgentSourcedCreator({
            creatorId: creator.id,
            campaignId,
            href: rejectLink.href,
            method: rejectLink.method,
          }),
        )
      }
    : undefined

  const handleUndo = undoLink
    ? (): void => {
        dispatch(
          undoAgentSourcedCreatorAction({
            creatorId: creator.id,
            campaignId,
            href: undoLink.href,
            method: undoLink.method,
          }),
        )
      }
    : undefined

  const handleShortlist = shortlistLink
    ? (): void => {
        dispatch(
          shortlistAgentSourcedCreator({
            creatorId: creator.id,
            campaignId,
            href: shortlistLink.href,
            method: shortlistLink.method,
          }),
        )
      }
    : undefined

  const handleUnshortlist = unshortlistLink
    ? (): void => {
        dispatch(
          unshortlistAgentSourcedCreator({
            creatorId: creator.id,
            campaignId,
            href: unshortlistLink.href,
            method: unshortlistLink.method,
          }),
        )
      }
    : undefined

  return (
    <>
      <Backdrop open={open} onClick={onClose} data-testid="drawer-backdrop" />
      <DrawerPanel open={open} role="dialog" aria-label="Creator profile details">
        <CloseButton onClick={onClose} aria-label="Close" data-testid="drawer-close">
          <Icon glyph="close-x" size={1.25} color={Theme.grey900Color} />
        </CloseButton>

        <Header
          creator={creator}
          onApprove={handleApprove}
          onReject={handleReject}
          onUndo={handleUndo}
          onShortlist={handleShortlist}
          onUnshortlist={handleUnshortlist}
          isActionLoading={isActionLoading}
          showDecisionPill={showDecisionPill}
          showSourcedEmail={showSourcedEmail}
        />

        <DrawerBody>
          <ScoreSummaryStack>
            <BannerLayer>
              <AIRationaleBanner
                summary={creator.ai_rationale?.summary}
                score={creator.ai_rationale?.selection_score}
              />
            </BannerLayer>

            <ScoreBreakdownTuck>
              <ScoreBreakdown
                scoreBreakdown={creator.ai_rationale?.score_breakdown}
                flags={creator.ai_rationale?.flags}
              />
            </ScoreBreakdownTuck>
          </ScoreSummaryStack>

          {creator.selected_works && creator.selected_works.length > 0 && (
            <DrawerSection>
              <DrawerSectionTitle>
                <FormattedMessage id="creatorSearch.drawer.selectedWorks.title" />
              </DrawerSectionTitle>
              <SelectedWorks creator={creator} works={creator.selected_works} />
            </DrawerSection>
          )}

          <AudiencePanel creator={creator} />
        </DrawerBody>
      </DrawerPanel>
    </>
  )
}

export { AgentSourcedCreatorDrawer }
export default AgentSourcedCreatorDrawer
